// Data Collection Period — the NCCD evidence window, which runs Aug → Jul and
// straddles two calendar/academic years. It is a SEPARATE dimension from the
// academic year (which is Jan–Dec and drives year levels); advancing the
// collection period must never change a student's year level.
//
// A period is keyed by its NCCD census year (the August census at the end of
// the window): period 2026 covers 1 Aug 2025 → 31 Jul 2026 and censuses in
// Aug 2026. Everything we collect is stamped with BOTH `collectionPeriod`
// (this) and the existing academic `year` — we add, never replace.

/** NCCD census year for a date. Aug or later → next calendar year, else this one. */
export function censusYearForDate(date = new Date()) {
  // getMonth() is 0-based; 7 === August.
  return date.getMonth() >= 7 ? date.getFullYear() + 1 : date.getFullYear();
}

/** The current collection period key (a census-year string, e.g. "2026"). */
export function currentCollectionPeriod(date = new Date()) {
  return String(censusYearForDate(date));
}

/** The active collection period — the manually-managed current window (set by
 *  the Settings → Data rollover), falling back to the date-derived one. */
export function activeCollectionPeriod() {
  return (typeof window !== 'undefined' && window.nccd?.activeCollectionPeriod)
    || currentCollectionPeriod();
}

/** ISO date bounds for a period: 1 Aug (year-1) → 31 Jul (year). */
export function collectionPeriodBounds(censusYear) {
  const y = parseInt(censusYear, 10);
  return { from: `${y - 1}-08-01`, to: `${y}-07-31` };
}

/** Which collection period a given ISO/date-ish value falls in (census-year key). */
export function inferCollectionPeriod(dateish) {
  if (!dateish) return currentCollectionPeriod();
  // Firestore Timestamps (e.g. a legacy meeting's createdAt) aren't accepted by
  // `new Date()` — unwrap them via toDate() first, else they'd read as Invalid
  // Date and wrongly collapse into the current period.
  const d = dateish instanceof Date
    ? dateish
    : (typeof dateish?.toDate === 'function' ? dateish.toDate() : new Date(dateish));
  if (isNaN(d)) return currentCollectionPeriod();
  return currentCollectionPeriod(d);
}

/** Split-year span for a period, e.g. census 2027 → "26/27" (Aug 2026 – Jul
 *  2027). The period STRADDLES two calendar years, so it displays as a span —
 *  unlike the academic year, which is one flat calendar year (e.g. "2026"). */
export function collectionPeriodSpan(censusYear) {
  const y = parseInt(censusYear, 10);
  if (isNaN(y)) return String(censusYear || '');
  return `${String(y - 1).slice(-2)}/${String(y).slice(-2)}`;
}

/** Short label, e.g. "NCCD 26/27". */
export function collectionPeriodShort(censusYear) {
  return `NCCD ${collectionPeriodSpan(censusYear)}`;
}

/** Full label, e.g. "NCCD 26/27 · Aug 2026 – Jul 2027". */
export function collectionPeriodLabel(censusYear) {
  const y = parseInt(censusYear, 10);
  if (isNaN(y)) return String(censusYear || '');
  return `NCCD ${collectionPeriodSpan(y)} · Aug ${y - 1} – Jul ${y}`;
}

/** The next period after `censusYear` (used by the rollover). */
export function nextCollectionPeriod(censusYear) {
  return String(parseInt(censusYear, 10) + 1);
}

/**
 * The NCCD submission deadline for a census year: the SECOND FRIDAY of August
 * of that year (period 2026 → 2nd Friday of Aug 2026). Returns a Date.
 */
export function submissionDeadline(censusYear) {
  const y = parseInt(censusYear, 10);
  const first = new Date(y, 7, 1);           // 1 Aug (month 7)
  const toFriday = (5 - first.getDay() + 7) % 7; // 5 = Friday
  return new Date(y, 7, 1 + toFriday + 7);   // first Friday + 7 = second Friday
}

/**
 * True once we're on/after the submission deadline for `censusYear`. This is
 * the ONLY thing that auto-advances the period — otherwise it stays put until
 * someone clicks the rollover button.
 */
export function isPastDeadline(censusYear, date = new Date()) {
  return date >= submissionDeadline(censusYear);
}

/** Friendly deadline label, e.g. "Fri 14 Aug 2026". */
export function submissionDeadlineLabel(censusYear) {
  const d = submissionDeadline(censusYear);
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Viewing filter ──────────────────────────────────────────────────────────
// Alongside the academic-year filter, the UI can filter DATED content by NCCD
// collection period. `window.nccd.viewCollectionPeriod` holds the selected
// period ('' / null = "all periods"). Set by the period selector in the header.

/** The period currently selected for VIEWING ('' = all). */
export function viewCollectionPeriod() {
  return (typeof window !== 'undefined' && window.nccd?.viewCollectionPeriod) || '';
}

/**
 * True if a dated item (note / meeting / profile section carrying a
 * `collectionPeriod` stamp) should be shown under the current view filter.
 * Items with no stamp fall back to inferring the period from a date field.
 */
export function itemInViewPeriod(item, dateField) {
  const view = viewCollectionPeriod();
  if (!view) return true; // "all periods"
  if (!item) return false;
  // A meeting's date lives at item.data.meetingDate (not item.meetingDate), so
  // include it in the fallback chain before createdAt, or unstamped legacy
  // meetings mis-bucket into the current period.
  const stamp = item.collectionPeriod || inferCollectionPeriod(
    dateField ? item[dateField] : (item.date || item.meetingDate || item.data?.meetingDate || item.createdAt));
  return String(stamp) === String(view);
}

/** All periods the school has on record, newest first (for the selector). */
export function periodsOnRecord() {
  const list = (typeof window !== 'undefined' && Array.isArray(window.nccd?.collectionPeriods)) ? window.nccd.collectionPeriods.slice() : [];
  const active = activeCollectionPeriod();
  if (active && !list.includes(active)) list.push(active);
  return Array.from(new Set(list)).sort().reverse();
}

/**
 * Add the active collection period to a write payload, alongside whatever year
 * field is already there — we never replace the academic year. Returns the
 * same object for chaining.
 */
export function stampCollectionPeriod(payload, period = activeCollectionPeriod()) {
  if (payload && typeof payload === 'object') payload.collectionPeriod = period;
  return payload;
}
